// src/components/WarRoomMatrices.js
//
// RULING 310 (Bob 2026-10-13, "yes"): every agreement's violation matrix, in the War Room of the committee that holds it.
// One rule at a time, like the slide decks. The General Chairperson confirms, corrects or strikes each entry, and chooses
// who sees the matrix: nobody beyond himself, his Local Chairpersons, or all the members of his committee. Only the
// entries he confirmed or corrected leave the War Room (the server builds matrixPublished; the agreements library shows
// it with "confirmed by the General Chairperson on [date]"). The quotation is the agreement's own words and is never
// edited here - a wrong one is struck. Every ruling and every release is kept; the last one governs.
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import RulingRef from './RulingRef';
import { newMatrixReview, newMatrixRelease, currentRulings, currentRelease, reviewTally, dateOf,
  DECISION_WORDS, AUDIENCES, AUDIENCE_WORDS } from '../data/matrixReview';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2', quote: '#F4E7C8' };
const DCOL = { confirmed: C.ok, corrected: C.accent, struck: C.bad };
const btn = (bg) => ({ background: bg, color: '#0B1626', border: 'none', borderRadius: 4, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' });
const ghost = { background: 'transparent', color: C.dim, border: '1px solid ' + C.line, borderRadius: 4, padding: '5px 10px', fontSize: 12, cursor: 'pointer' };
const field = { width: '100%', background: '#081120', color: C.text, border: '1px solid ' + C.line, borderRadius: 4, padding: 6, fontSize: 13, boxSizing: 'border-box' };

export default function WarRoomMatrices({ user, committee }) {
  const [decks, setDecks] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [releases, setReleases] = useState([]);
  const [err, setErr] = useState('');
  const [pick, setPick] = useState('');
  const [ruleAt, setRuleAt] = useState(0);
  const [openOnly, setOpenOnly] = useState(false);
  const [editing, setEditing] = useState(null);   // { entryId, decision, label, how, note }
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(false);
  // Forensic audit 2026-10-16: each read's failure, kept apart from `err` (the last write's refusal). A failed read of
  // the rulings used to show every entry "not yet ruled on" with Confirm/Correct/Strike live (a second ruling over one
  // he cannot see); a failed read of the releases showed "Not released." with the audience buttons live; a failed read
  // of the matrices showed "No matrix has been built". Each now shows the failure and holds what would act on it.
  const [readErr, setReadErr] = useState({});

  useEffect(() => {
    if (!committee || !shown) return undefined;
    setReadErr({});
    const failed = (k) => (e) => setReadErr((x) => ({ ...x, [k]: (e && (e.code || e.message)) || 'refused' }));
    const by = (c) => query(collection(db, c), where('committee', '==', committee));
    const u1 = onSnapshot(by('matrixDecks'), (s) => setDecks(s.docs.map(d => d.data()).sort((a, b) => String(a.title).localeCompare(String(b.title)))),
      (e) => { failed('decks')(e); setDecks(null); });
    const u2 = onSnapshot(by('matrixReviews'), (s) => setReviews(s.docs.map(d => d.data())), (e) => { failed('reviews')(e); setReviews([]); });
    const u3 = onSnapshot(by('matrixReleases'), (s) => setReleases(s.docs.map(d => d.data())), (e) => { failed('releases')(e); setReleases([]); });
    return () => { u1(); u2(); u3(); };
  }, [committee, shown]);
  const reviewsFailed = !!readErr.reviews;
  const releasesFailed = !!readErr.releases;

  const deck = useMemo(() => (decks || []).find(d => d.agreementKey === pick) || (decks || [])[0] || null, [decks, pick]);
  const mine = useMemo(() => reviews.filter(r => deck && r.agreementKey === deck.agreementKey), [reviews, deck]);
  const cur = useMemo(() => currentRulings(mine), [mine]);
  const tally = useMemo(() => reviewTally(deck, mine), [deck, mine]);
  const rel = useMemo(() => currentRelease(releases.filter(r => deck && r.agreementKey === deck.agreementKey)), [releases, deck]);
  const rules = useMemo(() => {
    if (!deck) return [];
    return (deck.rules || []).filter(rule => !openOnly || deck.entries.some(e => e.rule === rule && !cur[e.id]));
  }, [deck, openOnly, cur]);
  const at = Math.min(ruleAt, Math.max(0, rules.length - 1));
  const rule = rules[at];
  const entries = deck && rule ? deck.entries.filter(e => e.rule === rule && (!openOnly || !cur[e.id])) : [];

  const rule_ = async (entry, decision, extra) => {
    if (reviewsFailed) return;   // Forensic audit 2026-10-16
    setErr('');
    const r = newMatrixReview({ deck, entry, decision, ...(extra || {}), user, serverTime: serverTimestamp() });
    if (!r.ok) { setErr(r.errors.join(' ')); return; }
    const doc = { ...r.doc };
    if (!doc.note) delete doc.note;
    setBusy(true);
    try { await addDoc(collection(db, 'matrixReviews'), doc); setEditing(null); }
    catch (e) { setErr('Not recorded (' + (e.code || e.message) + ').'); }
    setBusy(false);
  };
  const release = async (audience) => {
    if (releasesFailed) return;   // Forensic audit 2026-10-16
    setErr('');
    const r = newMatrixRelease({ deck, audience, user, serverTime: serverTimestamp() });
    if (!r.ok) { setErr(r.errors.join(' ')); return; }
    setBusy(true);
    try { await addDoc(collection(db, 'matrixReleases'), r.doc); }
    catch (e) { setErr('Not recorded (' + (e.code || e.message) + ').'); }
    setBusy(false);
  };

  return (
    <div style={{ background: C.panel, border: '1px solid ' + C.line, borderRadius: 8, padding: 14, marginBottom: 16, color: C.text }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.accent }}>Violation matrices <RulingRef user={user} n={310} why="Every agreement's matrix is in the War Room; the General Chairperson confirms each entry and decides who sees it." /></div>
          <div style={{ fontSize: 12, color: C.dim }}>Your committee's agreements, one rule at a time. Confirm, correct or strike each entry; nothing leaves this room until you confirm it and release it.</div>
        </div>
        <button style={ghost} onClick={() => setShown(v => !v)}>{shown ? 'Close' : 'Open the matrices'}</button>
      </div>
      {shown && (
        <div style={{ marginTop: 12 }}>
          {err && <div style={{ color: C.bad, fontSize: 12, marginBottom: 8 }}>{err}</div>}
          {readErr.decks ? <div style={{ color: C.bad, fontSize: 12, marginBottom: 8 }}>&#9888; The matrices could not be read ({readErr.decks}). This is NOT "no matrix built" - reload the page.</div>
            : decks === null ? <div style={{ fontSize: 12, color: C.dim }}>Reading…</div>
            : !decks.length ? <div style={{ fontSize: 12, color: C.dim }}>No matrix has been built for this committee's agreements yet.</div>
            : (
              <>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                  <select aria-label="Which agreement to show" value={deck ? deck.agreementKey : ''} onChange={(e) => { setPick(e.target.value); setRuleAt(0); setEditing(null); }} style={{ ...field, width: 'auto', maxWidth: '100%' }}>
                    {decks.map(d => <option key={d.agreementKey} value={d.agreementKey}>{d.title}</option>)}
                  </select>
                  <label style={{ fontSize: 12, color: C.dim }}><input aria-label="Only entries not yet ruled on" type="checkbox" checked={openOnly} onChange={(e) => { setOpenOnly(e.target.checked); setRuleAt(0); }} /> Only entries not yet ruled on</label>
                </div>
                {reviewsFailed ? (
                  <div style={{ fontSize: 12, color: C.bad, marginBottom: 8 }}>&#9888; Your rulings could not be read ({readErr.reviews}). This is NOT "not yet ruled on": what you have ruled is unknown, so nothing can be ruled here until it can be read. Reload the page.</div>
                ) : (
                <div style={{ fontSize: 12, color: C.dim, marginBottom: 8 }}>
                  {tally.total} entries · <span style={{ color: C.ok }}>{tally.confirmed} confirmed</span> · <span style={{ color: C.accent }}>{tally.corrected} corrected</span> · <span style={{ color: C.bad }}>{tally.struck} struck</span> · {tally.open} not yet ruled on
                </div>
                )}
                <div style={{ border: '1px solid ' + C.line, borderRadius: 6, padding: 10, marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Who may see this matrix</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {AUDIENCES.map(a => {
                      const on = !releasesFailed && (rel ? rel.audience : 'gc') === a;
                      return <button key={a} disabled={busy || on || releasesFailed} style={on ? btn(C.accent) : ghost} onClick={() => release(a)}>{AUDIENCE_WORDS[a]}</button>;
                    })}
                  </div>
                  <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>
                    {/* Forensic audit 2026-10-16: never "Not released." from a read that failed. */}
                    {releasesFailed ? <span style={{ color: C.bad }}>&#9888; Who may see this matrix could not be read ({readErr.releases}); it is unknown, not "not released", and cannot be changed until it can be read. </span>
                      : rel ? 'Set by ' + (rel.byName || 'the General Chairperson') + ' on ' + dateOf(rel.at) + '. ' : 'Not released. '}
                    Only entries you confirmed or corrected are shown to anyone else, in the agreements library, each marked "confirmed by the General Chairperson on [date]".
                  </div>
                </div>
                {reviewsFailed ? null : !rule ? <div style={{ fontSize: 12, color: C.dim }}>Every entry of this matrix has a ruling.</div> : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <button style={ghost} disabled={at === 0} onClick={() => { setRuleAt(at - 1); setEditing(null); }}>‹ Previous rule</button>
                      <div style={{ fontSize: 12, color: C.dim }}>Rule {at + 1} of {rules.length}</div>
                      <button style={ghost} disabled={at >= rules.length - 1} onClick={() => { setRuleAt(at + 1); setEditing(null); }}>Next rule ›</button>
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{rule}</div>
                    {entries.map(e => {
                      const r = cur[e.id];
                      const ed = editing && editing.entryId === e.id ? editing : null;
                      return (
                        <div key={e.id} style={{ borderTop: '1px solid ' + C.line, padding: '10px 0' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, textTransform: 'uppercase' }}>
                            {r && r.decision === 'corrected' && r.label ? r.label : e.label}{e.remedyCap ? <span style={{ color: C.bad }}> · remedy capped by the rule</span> : null}
                          </div>
                          <div style={{ fontSize: 13, margin: '4px 0' }}>{r && r.decision === 'corrected' && r.how ? r.how : e.how}</div>
                          <div style={{ fontSize: 13, fontStyle: 'italic', color: C.quote, borderLeft: '3px solid ' + C.accent, paddingLeft: 8 }}>“{e.quote}”</div>
                          <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>Line {e.sourceLine} · {e.provenance === 'EXTRACTED-UNCONFIRMED' ? 'extracted, not yet confirmed' : String(e.provenance || '').toLowerCase().replace(/-/g, ' ')}</div>
                          {r && <div style={{ fontSize: 12, color: DCOL[r.decision], marginTop: 4 }}>{DECISION_WORDS[r.decision]} by {r.byName || 'the General Chairperson'} on {dateOf(r.at)}{r.note ? ' - ' + r.note : ''}</div>}
                          {!ed ? (
                            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                              <button style={btn(C.ok)} disabled={busy} onClick={() => rule_(e, 'confirmed')}>Confirm</button>
                              <button style={btn(C.accent)} disabled={busy} onClick={() => setEditing({ entryId: e.id, decision: 'corrected', label: e.label, how: e.how, note: '' })}>Correct</button>
                              <button style={btn(C.bad)} disabled={busy} onClick={() => setEditing({ entryId: e.id, decision: 'struck', note: '' })}>Strike</button>
                            </div>
                          ) : (
                            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {ed.decision === 'corrected' && (
                                <>
                                  <div style={{ fontSize: 11, color: C.dim }}>Correct the platform's words. The quotation is the agreement's own and is not edited; if it is wrong, strike the entry.</div>
                                  <input aria-label="The violation, as it should read" style={field} value={ed.label} onChange={(x) => setEditing({ ...ed, label: x.target.value })} placeholder="The violation, as it should read" />
                                  <textarea aria-label="How it is violated" style={field} rows={3} value={ed.how} onChange={(x) => setEditing({ ...ed, how: x.target.value })} placeholder="How it is violated" />
                                </>
                              )}
                              <textarea aria-label="Note on this rule" style={field} rows={2} value={ed.note} onChange={(x) => setEditing({ ...ed, note: x.target.value })} placeholder={ed.decision === 'struck' ? 'Why it is struck (required)' : 'Note (optional)'} />
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button style={btn(DCOL[ed.decision])} disabled={busy} onClick={() => rule_(e, ed.decision, { label: ed.label, how: ed.how, note: ed.note })}>{ed.decision === 'struck' ? 'Strike it' : 'Record the correction'}</button>
                                <button style={ghost} onClick={() => setEditing(null)}>Cancel</button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            )}
        </div>
      )}
    </div>
  );
}
